import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Dialog, DialogContent } from "@mui/material";

export const Container = styled.div`
    background-color: #29384D;
    padding: 20px;
    border-radius: 8px;
    width: 100%;
    color: #ffffff;
    font-family: Arial, sans-serif;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    min-width: 0;
    ${({onClick}) =>
            onClick &&
            css`
                cursor: pointer;
            `} @media (max-width: ${({theme}) => theme.breakpoints.mobile}) {
    padding: 16px;
}

    ${({$embargoed}) =>
            $embargoed &&
            css`
                border: 1px solid #e5484d;
                box-shadow: 0 0 0 1px rgba(229, 72, 77, 0.28),
                0 6px 18px rgba(229, 72, 77, 0.12);
                /* Clear a band along the top edge so the glued badge never sits on the
                   title or the rating stars. Declared after the mobile query above so it
                   survives the shorthand padding reset there. */
                padding-top: 36px;

                @media (max-width: ${({theme}) => theme.breakpoints.mobile}) {
                    padding-top: 32px;
                }
            `}
`;

/**
 * The small red rectangle glued into the top-right corner of an embargoed package block.
 * Its outer corners are square so it reads as fused to the card border; only the inner
 * corners are rounded.
 */
export const EmbargoBadge = styled.span`
    position: absolute;
    top: -1px;
    right: -1px;
    display: inline-flex;
    align-items: center;
    max-width: calc(100% - 24px);
    padding: 4px 10px;
    background: #e5484d;
    color: #ffffff;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    border-radius: 0 8px 0 8px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const PackageWrapper = styled.div`
  display: flex;
  width: 100%;
  max-width: 100%;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  align-items: center;
  min-width: 0;

  > * {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  @media screen and (min-width: 1008px) {
    ${({ packagesView }) =>
        packagesView === "grid" &&
        css`
          flex-direction: row;
          flex-wrap: wrap;
          justify-content: flex-start;
          align-items: flex-start;

          > * {
            flex: 1 1 400px;
            max-width: 400px;
            align-self: stretch;
            display: flex;
            flex-direction: column;

            &:nth-of-type(n + 3) {
              margin-top: 5px;
            }
          }
        `}
  }
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;
  min-width: 0;
`;

export const Title = styled.h2`
  color: ${({ color }) => color || "#7C74CF"};
  font-size: 18px;
`;

export const Description = styled.p`
    font-size: 14px;
    color: #ffffff;
    margin-bottom: 12px;
    min-width: 0;
    overflow-wrap: anywhere;
    flex: 1 1 auto;
`;

export const RatingSection = styled.div`
    display: flex;
    align-items: flex-start;
    align-self: flex-start;
    flex: 0 0 auto;
    margin-top: 4px;
`;

export const Star = styled.span`
  color: ${(props) => (props.filled ? "#FFD700" : "#666666")};
  font-size: 20px;
  margin-right: 2px;
`;

export const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  font-size: 12px;
  color: #d9d9e3;
  margin-top: 16px;
  padding-right: 20px;
  gap: 12px;
  min-width: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding-right: 0;
    flex-wrap: wrap;
    justify-content: space-between;
  }
`;

export const FooterMeta = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-width: 0;
    max-width: 100%;
    width: 100%;

    ${({ $stackFeedback }) =>
            $stackFeedback &&
            css`
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
            `}

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    width: 100%;
}
`;

export const UploadTime = styled.div`
    font-size: 12px;
    color: #d9d9e3;
    text-align: right;
    min-width: 0;
    max-width: 100%;
    margin-left: auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    ${({ $stackFeedback }) =>
            $stackFeedback &&
            css`
                text-align: left;
                white-space: normal;
                overflow: visible;
                text-overflow: unset;
                width: 100%;
                margin-left: 0;
            `}

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    text-align: left;
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    width: 100%;
    margin-left: 0;
    flex: 1 1 100%;
}
`;

export const Highlight = styled.span`
  color: ${({ theme }) => theme.palette.secondary.main};
`;

export const PackageDialog = styled(Dialog)`
  > div > .MuiBackdrop-root {
    background: rgba(0, 0, 0, 0.55);
    transition: opacity 160ms ease;
  }

  > div > .MuiPaper-root {
    margin: 32px;
    width: 100%;
    max-width: 1090px;
    border-radius: 8px;
    overflow: hidden;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
      margin: 0;
      width: 100%;
      max-width: 100%;
      height: 100%;
      max-height: 100%;
      border-radius: 0;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      position: fixed;
    }
  }
`;

export const PackageDialogContent = styled(DialogContent)`
  background-color: #29384D;
  padding: 10px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 100%;
  }
`;

export const PackageCloseWrap = styled.div`
  position: sticky;
  top: 0;
  z-index: 3;
  height: 24px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 0 12px;
  background: #29384D;
`;

export const PackageDialogDelimiter = styled.hr`
  border-color: #8095b72b;
  border-width: 1px 0 0;
  margin: 0;
`;

export const PackageDetailsContainer = styled.div`
  padding: 24px;
  width: 100%;
  max-width: 100%;
  min-width: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 16px;
  }
`;

export const PackageDetailsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

export const PackageDetailsSwitcher = styled.div`
    border: 1px solid #5d62b6;
    background: #202c3dbf;
    display: flex;
    border-radius: 8px;
    min-width: 0;
    overflow: hidden;
`;

export const PackageDetailsSwitcherItem = styled.div`
  padding: 8px;
  border-radius: inherit;
  cursor: pointer;
  flex: 1 1 0;
  text-align: center;
  min-width: 0;

  ${({ isActive }) =>
      isActive &&
      css`
      background: #5d62b6;
      box-shadow: inset 2px 4px 4px #00000040;
      cursor: initial;
    `}
`;

export const PackageDetailsContent = styled.div`
  padding: 16px 0;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
`;

export const PackageLoaderWrap = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 16px 0;
  }
`;

export const DescriptionRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    min-width: 0;
    width: 100%;

    ${({ $stackFeedback }) =>
            $stackFeedback &&
            css`
      flex-direction: column;
      align-items: stretch;
    `}

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 10px;
}
`;

export const FeedbackBlock = styled.div`
    flex: 0 0 260px;
    max-width: 320px;
    min-width: 0;
    background: rgba(32, 44, 61, 0.75);
    border: 1px solid rgba(128, 149, 183, 0.18);
    border-radius: 8px;
    padding: 10px 12px;

    ${({ $stackFeedback }) =>
            $stackFeedback &&
            css`
      flex: 1 1 auto;
      width: 100%;
      max-width: 100%;
    `}

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex: 1 1 auto;
    width: 100%;
    max-width: 100%;
}
`;

export const FeedbackLabel = styled.div`
  font-size: 11px;
  line-height: 1.2;
  color: #d9d9e3;
  opacity: 0.9;
  margin-bottom: 6px;
`;

export const FeedbackText = styled.div`
  font-size: 13px;
  line-height: 1.35;
  color: #ffffff;
  min-width: 0;
  overflow-wrap: anywhere;
`;

export const PackageDetailsMetadata = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px 28px;
    padding: 16px 0 20px;
    margin-bottom: 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

export const PackageDetailsMetadataItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 100px;
`;

export const PackageDetailsMetadataLabel = styled.span`
    color: ${({ theme }) => theme.palette.secondary.main};
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;

    ${({ $alert }) =>
        $alert &&
        css`
      color: #e5484d;
    `}
`;

export const PackageDetailsMetadataValue = styled.span`
    font-size: 14px;
    font-weight: 500;

    ${({ $alert }) =>
        $alert &&
        css`
      color: #e5484d;
      font-weight: 700;
    `}
`;

export const PackageDetailsNotes = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 0 0 20px;
    margin-bottom: 12px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    min-width: 0;

    ${({ $standalone }) =>
        $standalone &&
        css`
      padding-top: 16px;
      border-top: 1px solid rgba(255, 255, 255, 0.08);
    `}
`;

export const PackageDetailsNotesLabel = styled.span`
    color: ${({ theme }) => theme.palette.secondary.main};
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
`;

export const PackageDetailsNotesText = styled.p`
    margin: 0;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.45;
    color: #ffffff;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
`;
